import { json, type LoaderFunctionArgs } from "@remix-run/node"
import { Link, Outlet, useLoaderData, useLocation } from "@remix-run/react"
import { Public } from "~/utils/remix"
import { Title } from "~/components/header"
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar"
import dayjs from "dayjs"
import defaultAvatar from "~/assets/default-avatar.webp"
import { FollowButton } from "~/components/follow"
import { getImage } from "~/utils/image.server"
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs"
import { Separator } from "~/components/ui/separator"
import showdown from "showdown"
import Typography from "~/components/typography"
import { cleanNulls, parseSocialLinks, Pronouns } from "~/utils/data"
import { InstagramLogoIcon, TwitterLogoIcon } from "@radix-ui/react-icons"
import { FacebookIcon } from "~/components/facebook-icon"
import { StarEnjoyedCount, StarVideosCount } from "~/models/videos.server"
import { FindStar, StarPostsCount, UserFollowing } from "~/models/users.server"

export const loader = Public(async ({ request, context, params }: LoaderFunctionArgs) => {
  if (!params.username) {
    throw json({ user: null }, { status: 404 })
  }

  const star = await FindStar(context, params.username)
  if (!star) {
    throw json({ user: null }, { status: 404 })
  }

  const following = context.user ? await UserFollowing(context, star.UserID) : null
  const videoCount = await StarVideosCount(context, star.UserID)
  const enjoyedCount = await StarEnjoyedCount(context, star.UserID)
  const socialCount = await StarPostsCount(context, star.UserID)

  return json({
    videoCount,
    socialCount,
    enjoyedCount,
    star: {
      UserID: star.UserID,
      UserName: star.UserName,
      DisplayName: star.DisplayName,
      CaptivatedBy: star.CaptivatedBy,
      NeedToLive: star.NeedToLive,
      GratefulFor: star.GratefulFor,
      CreatedAt: star.CreatedAt,
      Avatar: getImage(star.Avatar, defaultAvatar),
      Pronouns: JSON.parse(cleanNulls(star.Pronouns, "[]")),
      SocialLink: parseSocialLinks(star.SocialLink),
    },
    follow: following,
  })
})

export default function Stars() {
  const data = useLoaderData<typeof loader>()
  const converter = new showdown.Converter()
  const location = useLocation()
  const star = data.star
  const tabs = [
    {
      href: `/makelovenotpornstars/${star.UserName}/videos`,
      label: `Videos (${data.videoCount})`,
      value: "videos",
    },
    {
      href: `/makelovenotpornstars/${star.UserName}/social`,
      label: `Social (${data.socialCount})`,
      value: "social",
    },
    {
      href: `/makelovenotpornstars/${star.UserName}/enjoying`,
      label: `Enjoyed Videos (${data.enjoyedCount})`,
      value: "enjoyed",
    },
  ]

  const selectedTab = tabs.filter((tab) => {
    return tab.href === location.pathname
  })[0]

  return (
    <div className="mt-32">
      <div className="bg-primary/5 border-2 border-primary/10 text-accent-foreground rounded-lg p-4 pt-6 mx-4 md:mx-16">
        <div className="w-full flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap justify-start items-center gap-8">
            <Avatar className="h-28 w-28">
              <AvatarImage src={star.Avatar} className="object-cover" alt="avatar" />
              <AvatarFallback>JA</AvatarFallback>
            </Avatar>
            <div className="flex flex-col justify-start gap-2">
              <div className="flex justify-start items-center gap-4 mb-2">
                <Title className="mr-2" content={star.DisplayName || star.UserName} />
                {star.SocialLink?.Twitter && (
                  <Link to={`https://twitter.com/${star.SocialLink?.Twitter}`} className="pt-1" target="_blank">
                    <TwitterLogoIcon className="h-6 w-6" />
                  </Link>
                )}
                {star.SocialLink?.Instagram && (
                  <Link to={`https://instagram.com/${star.SocialLink?.Instagram}`} className="pt-1" target="_blank">
                    <InstagramLogoIcon className="h-6 w-6" />
                  </Link>
                )}
                {star.SocialLink?.Facebook && (
                  <Link to={`https://facebook.com/${star.SocialLink?.Facebook}`} className="pt-1" target="_blank">
                    <FacebookIcon />
                  </Link>
                )}
              </div>
              <p className="text-lg">
                Member of the #SocialSexRevolution since: {dayjs(star.CreatedAt).format("MMMM, DD YYYY")}
              </p>
              <p className="text-sm">
                {star.Pronouns.map((pronoun: any, index: number) => (
                  <span key={index}>
                    <b>{pronoun.Name}:</b>{" "}
                    {
                      Pronouns.filter((item) => {
                        return parseInt(item.value) === pronoun.Gender
                      })[0]?.label
                    }
                    &nbsp;&nbsp;&nbsp;&nbsp;
                  </span>
                ))}
              </p>
            </div>
          </div>
          <FollowButton className="p-6 mr-4 text-lg" starID={star.UserID} follow={data.follow} />
        </div>
        <Separator className="my-6 mx-4 w-auto border-[1px] border-primary/30 rounded-full" />
        <div className="mx-4 md:grid md:grid-cols-3 md:gap-8">
          <div>
            <h3 className="scroll-m-20 text-md font-bold tracking-tight">Captivated By:</h3>
            <Typography>
              <div
                dangerouslySetInnerHTML={{
                  __html: converter.makeHtml(star.CaptivatedBy),
                }}
              />
            </Typography>
          </div>
          <div>
            <h3 className="scroll-m-20 text-md font-bold tracking-tight">Can’t live without:</h3>
            <Typography>
              <div
                dangerouslySetInnerHTML={{
                  __html: converter.makeHtml(star.NeedToLive),
                }}
              />
            </Typography>
          </div>
          <div>
            <h3 className="scroll-m-20 text-md font-bold tracking-tight">Deeply grateful for:</h3>
            <Typography>
              <div
                dangerouslySetInnerHTML={{
                  __html: converter.makeHtml(star.GratefulFor),
                }}
              />
            </Typography>
          </div>
        </div>
      </div>

      <Tabs defaultValue={selectedTab?.value} className="flex justify-center items-center mt-8">
        <TabsList>
          {tabs.map((tab, index) => (
            <Link key={index} to={tab.href} preventScrollReset>
              <TabsTrigger key={index} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            </Link>
          ))}
        </TabsList>
      </Tabs>

      <Outlet />
    </div>
  )
}
